import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getScopeLabel,
    getReviewStatusLabel,
    getTaskStatusLabel,
    getTaskTypeLabel,
    getRouteStatusLabel,
    getEvidenceStatusLabel,
    PMO_METRIC_LABELS,
    PMO_SCOPE_LABELS,
} from "../utils/pmoLabels";
import {
    PMO_BADGE_TONES,
    getPmoTooltip,
    getTaskStatusTone,
    getReviewStatusTone,
    getRouteStatusTone,
    getEvidenceStatusTone,
    getHealthTone,
} from "../utils/pmoStyles";
import { API_BASE } from "../config/orbisConfig";


function safeNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function pct(value) {
    return Math.max(0, Math.min(100, safeNumber(value)));
}

function cleanText(value) {
    return String(value || "")
        .replaceAll("Construcci?n", "Construcción")
        .replaceAll("operaci", "operación")
        .replaceAll("bit?cora", "bitácora")
        .replaceAll("atenci�n", "atención")
        .replaceAll("Atenci�n", "Atención")
        .replaceAll("Presi�n", "Presión")
        .replaceAll("presi�n", "presión");
}

function label(value) {
    return String(value || "sin estado")
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(value) {
    if (!value) return "Sin fecha";
    const d = new Date(`${value}T00:00:00`);
    if (Number.isNaN(d.getTime())) return value;

    return d.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function initials(name = "Usuario") {
    return String(name)
        .trim()
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((x) => x[0]?.toUpperCase())
        .join("") || "U";
}

function severityStyle(severity) {
    const s = String(severity || "").toLowerCase();

    if (s === "critical") {
        return { label: "Crítica", bg: "#FFF1F2", color: "#BE123C", border: "#FECDD3", accent: "#DC2626", icon: "!" };
    }

    if (s === "high") {
        return { label: "Alta", bg: "#FFF7ED", color: "#C2410C", border: "#FDBA74", accent: "#EA580C", icon: "▲" };
    }

    if (s === "medium") {
        return { label: "Media", bg: "#FFFBEB", color: "#B45309", border: "#FDE68A", accent: "#F59E0B", icon: "◐" };
    }

    return { label: "Baja", bg: "#F8FAFC", color: "#475569", border: "#E2E8F0", accent: "#64748B", icon: "○" };
}

function priorityStyle(priority) {
    const p = String(priority || "").toLowerCase();

    if (p === "critica") return { bg: "#FFF1F2", color: "#BE123C", border: "#FECDD3" };
    if (p === "alta") return { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" };
    if (p === "media") return { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" };

    return { bg: "#F8FAFC", color: "#475569", border: "#E2E8F0" };
}

function categoryLabel(category) {
    const map = {
        critical: "Crítica",
        overdue: "Vencimiento",
        review: "Revisión",
        governance: "Gobierno",
        planning: "Planeación",
        evidence: "Evidencia",
        geo: "Geoevidencia",
        inconsistent: "Inconsistencia",
        blocked: "Bloqueo",
        follow_up: "Seguimiento",
    };

    return map[String(category || "").toLowerCase()] || "Seguimiento";
}


function severityTooltip(severity) {
    const s = String(severity || "").toLowerCase();

    const map = {
        critical: "Severidad crítica: requiere atención inmediata porque concentra riesgo alto para fechas, cierres, revisión o trazabilidad.",
        high: "Severidad alta: requiere intervención próxima para evitar que se convierta en bloqueo crítico.",
        medium: "Severidad media: requiere seguimiento PMO, pero no representa una emergencia crítica.",
        low: "Severidad baja: hay una observación operativa menor que debe corregirse para mantener trazabilidad.",
    };

    return map[s] || map.low;
}

function categoryTooltip(category) {
    const c = String(category || "").toLowerCase();

    const map = {
        critical: "Acción crítica: concentra condiciones que pueden afectar el cierre ejecutivo del proyecto.",
        overdue: "La tarea está fuera de fecha y sigue abierta. Debe cerrarse, reprogramarse o justificarse.",
        review: "La tarea requiere decisión del revisor para aprobar, rechazar o solicitar cambios.",
        governance: "Acción de gobierno PMO: falta una definición básica como revisor, responsable o criterio de control.",
        planning: "Acción de planeación: falta fecha objetivo o una definición necesaria para controlar la tarea.",
        evidence: "La tarea requiere evidencia documental o visual para respaldar el avance registrado.",
        geo: "La tarea requiere evidencia con ubicación, pero aún no tiene georreferencia capturada.",
        inconsistent: "Hay una diferencia entre avance y estado. Revisa si debe cerrarse, enviarse a revisión o corregirse.",
        blocked: "La tarea está bloqueada por dependencia, ruta operativa o condición pendiente.",
        follow_up: "Acción de seguimiento: conviene revisar la tarea para evitar deuda operativa.",
    };

    return map[c] || map.follow_up;
}

function priorityTooltip(priority) {
    const p = String(priority || "").toLowerCase();

    const map = {
        critica: "Prioridad crítica: tarea de alto impacto para el proyecto o su ruta de cierre.",
        alta: "Prioridad alta: tarea relevante que debe mantenerse bajo seguimiento cercano.",
        media: "Prioridad media: tarea relevante para la operación, sin nivel crítico o alto asignado.",
        baja: "Prioridad baja: tarea de seguimiento ordinario o menor impacto operativo.",
    };

    return map[p] || "Prioridad operativa registrada para esta tarea.";
}

function scoreTooltip() {
    return "Puntaje de decisión calculado por vencimiento, prioridad, revisión, bloqueo, evidencia, geoevidencia e inconsistencias.";
}

function progressTooltip(action) {
    const progress = safeNumber(action?.progress_percent);
    if (progress >= 90 && String(action?.status || "").toLowerCase() !== "terminada") {
        return "Avance operativo alto. Si la tarea sigue abierta, conviene revisar si debe cerrarse, enviarse a revisión o reprogramarse.";
    }
    return "Avance operativo registrado en la tarea.";
}

function dueDateTooltip(action) {
    if (!action?.due_date) return "La tarea no tiene fecha objetivo; debe definirse para controlar el seguimiento PMO.";

    const days = safeNumber(action?.days_overdue);
    if (days > 0) {
        return "Fecha objetivo vencida. La tarea requiere decisión de cierre, ajuste o reprogramación.";
    }

    return "Fecha objetivo de la tarea para seguimiento PMO.";
}

function flagTooltip(flag) {
    const f = String(flag || "").toLowerCase();

    if (f.includes("vencida")) return "La tarea superó su fecha objetivo y aún no está cerrada. Requiere decisión de cierre, ajuste o reprogramación.";
    if (f.includes("sin geoevidencia") || f.includes("geo")) return "Existe evidencia o avance, pero falta ubicación capturada para cumplir la trazabilidad geográfica requerida.";
    if (f.includes("sin evidencia") || f.includes("evidencia")) return "La tarea requiere evidencia para respaldar el avance registrado.";
    if (f.includes("revisor")) return "La tarea necesita un revisor asignado para cerrar correctamente el ciclo de revisión.";
    if (f.includes("fecha")) return "La tarea necesita fecha objetivo para poder controlarse en la vista PMO.";
    if (f.includes("inconsistente")) return "El avance y el estado no coinciden. Revisa si la tarea debe cerrarse, enviarse a revisión o corregirse.";
    if (f.includes("revisión")) return "La tarea tiene un ciclo de revisión pendiente o requiere decisión del revisor.";

    return "Condición detectada por el Centro de Decisiones PMO.";
}

function recommendedActionTooltip() {
    return "Recomendación automática para resolver la condición detectada por el Centro de Decisiones PMO.";
}

function filterTooltip(key) {
    const map = {
        all: "Muestra todas las acciones detectadas por el Centro de Decisiones PMO.",
        critical: "Muestra solo acciones críticas que requieren atención inmediata.",
        high: "Muestra acciones altas que requieren intervención próxima.",
        medium: "Muestra acciones medias que requieren seguimiento PMO.",
        low: "Muestra observaciones bajas de trazabilidad o seguimiento.",
    };

    return map[key] || map.all;
}


function decisionFilterTooltip(key) {
    const map = {
        all: "Muestra todas las acciones detectadas por ORBIS.",
        blocked: "Muestra tareas bloqueadas que requieren intervención PMO para liberar la ejecución.",
        overdue: "Muestra tareas vencidas que requieren cierre, reprogramación o justificación.",
        evidence: "Muestra tareas sin evidencia documental suficiente para sostener el avance.",
        geo: "Muestra tareas con geoevidencia pendiente o trazabilidad territorial incompleta.",
        review: "Muestra tareas pendientes de dictamen del revisor.",
    };

    return map[key] || map.all;
}

function actionMatchesDecisionFilter(action, filter) {
    if (!filter || filter === "all") return true;

    const category = String(action?.category || "").toLowerCase();
    const status = String(action?.status || "").toLowerCase();
    const reviewStatus = String(action?.review_status || "").toLowerCase();
    const flagsText = (action?.flags || []).join(" ").toLowerCase();

    if (filter === "blocked") {
        return status === "bloqueada" || category === "blocked" || flagsText.includes("bloqueada") || flagsText.includes("bloqueo");
    }

    if (filter === "overdue") {
        return category === "overdue" || safeNumber(action?.days_overdue) > 0 || flagsText.includes("vencida");
    }

    if (filter === "evidence") {
        return category === "evidence" || flagsText.includes("sin evidencia") || flagsText.includes("evidencia");
    }

    if (filter === "geo") {
        return category === "geo" || flagsText.includes("geoevidencia") || flagsText.includes("sin geo");
    }

    if (filter === "review") {
        return category === "review" || reviewStatus === "en_revision" || reviewStatus === "pendiente_revision" || flagsText.includes("pendiente de revisión");
    }

    return true;
}

function KpiCard({ title, value, subtitle, color = "#0F172A", icon = "◈" }) {
    return (
        <div
            style={{
                borderRadius: 24,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                padding: 18,
                boxShadow: "0 18px 38px rgba(15,23,42,.06)",
                minHeight: 132,
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    right: -34,
                    top: -34,
                    width: 110,
                    height: 110,
                    borderRadius: 999,
                    background: `${color}13`,
                }}
            />

            <div style={{ position: "relative", display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                    <div
                        style={{
                            color: "#64748B",
                            fontSize: 11,
                            fontWeight: 950,
                            textTransform: "uppercase",
                            letterSpacing: ".04em",
                        }}
                    >
                        {title}
                    </div>

                    <div
                        style={{
                            marginTop: 10,
                            color,
                            fontSize: 34,
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                            lineHeight: 1,
                        }}
                    >
                        {value}
                    </div>

                    <div
                        style={{
                            marginTop: 10,
                            color: "#64748B",
                            fontSize: 12,
                            fontWeight: 750,
                            lineHeight: 1.35,
                        }}
                    >
                        {subtitle}
                    </div>
                </div>

                <div
                    style={{
                        width: 44,
                        height: 44,
                        borderRadius: 16,
                        background: `${color}12`,
                        color,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: 950,
                        border: `1px solid ${color}22`,
                    }}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
}

function Pill({ children, tone = "neutral", title }) {
    const styles = PMO_BADGE_TONES;
    const s = styles[tone] || styles.neutral;
    const tooltip = title || getPmoTooltip(children);

    return (
        <span
            title={tooltip}
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "6px 9px",
                borderRadius: 999,
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                fontSize: 10,
                fontWeight: 950,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </span>
    );
}

function ProgressBar({ value, color = "#2563EB", height = 10 }) {
    const v = pct(value);

    return (
        <div
            style={{
                height,
                borderRadius: 999,
                background: "#E2E8F0",
                overflow: "hidden",
                border: "1px solid #E2E8F0",
            }}
        >
            <div
                style={{
                    width: `${v}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: color,
                    boxShadow: `0 8px 18px ${color}40`,
                }}
            />
        </div>
    );
}

function UserMini({ name }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #2563EB, #7C3AED)",
                    color: "#FFFFFF",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 950,
                    flex: "0 0 auto",
                }}
            >
                {initials(name)}
            </div>
            <div
                style={{
                    color: "#334155",
                    fontSize: 11,
                    fontWeight: 850,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                }}
            >
                {name || "Sin responsable"}
            </div>
        </div>
    );
}

function buildDecisionInsight({ summary, actions, selectedProjectName }) {
    const total = safeNumber(summary.total_actions);
    const critical = safeNumber(summary.critical_actions);
    const high = safeNumber(summary.high_actions);
    const overdue = safeNumber(summary.overdue_actions);
    const governance = safeNumber(summary.governance_actions);
    const evidence = safeNumber(summary.evidence_actions);

    const top = actions?.[0] || null;
    const criticalList = actions.filter((a) => a.severity === "critical");
    const highList = actions.filter((a) => a.severity === "high");

    let title = "Operación en seguimiento";
    let message = "La operación tiene acciones detectadas, pero no concentra señales críticas dominantes. Mantén seguimiento preventivo.";
    let action = "Revisar acciones medias y bajas para evitar que se conviertan en deuda operativa.";
    let tone = { bg: "#ECFDF5", border: "#A7F3D0", color: "#047857", icon: "✓" };

    if (critical > 0) {
        title = "Decisiones críticas pendientes";
        message = "Existen tareas que concentran alta fricción operativa: vencimiento, prioridad crítica, falta de revisor o avance inconsistente. Estas acciones deben atenderse antes de abrir nuevas cargas.";
        action = "Atender primero las acciones críticas, asignar revisor donde falte, validar evidencia y cerrar tareas con avance alto.";
        tone = { bg: "#FFF1F2", border: "#FECDD3", color: "#BE123C", icon: "!" };
    } else if (high > 0 || overdue >= 8) {
        title = "Presión operativa alta";
        message = "La consulta muestra acumulación de vencimientos o acciones altas que pueden afectar fechas, cierres y trazabilidad PMO.";
        action = "Resolver vencimientos de mayor antigüedad y regularizar tareas sin evidencia, sin revisor o sin cierre formal.";
        tone = { bg: "#FFFBEB", border: "#FDE68A", color: "#B45309", icon: "◐" };
    }

    const bullets = [];

    if (total > 0) bullets.push(`${total} acción(es) requieren decisión o seguimiento PMO.`);
    if (critical > 0) bullets.push(`${critical} acción(es) crítica(s) deben atenderse primero.`);
    if (high > 0) bullets.push(`${high} acción(es) de severidad alta requieren intervención próxima.`);
    if (overdue > 0) bullets.push(`${overdue} acción(es) están asociadas a vencimientos.`);
    if (governance > 0) bullets.push(`${governance} acción(es) son de gobierno: sin revisor o sin fecha objetivo.`);
    if (evidence > 0) bullets.push(`${evidence} acción(es) requieren evidencia o geoevidencia.`);
    if (top) bullets.push(`Principal decisión: ${top.wbs_code ? `${top.wbs_code} · ` : ""}${top.task_name} (${safeNumber(top.decision_score).toFixed(0)} pts).`);

    if (!bullets.length) bullets.push("No se detectan decisiones pendientes relevantes en esta consulta.");

    return {
        title,
        message,
        action,
        tone,
        bullets,
        selectedProjectName: selectedProjectName || PMO_SCOPE_LABELS.portfolio,
        criticalList,
        highList,
    };
}

function getDecisionRouteContext(action = {}) {
    const category = String(action?.category || "").toLowerCase();
    const status = String(action?.status || "").toLowerCase();
    const reviewStatus = String(action?.review_status || "").toLowerCase();
    const flagsText = (action?.flags || []).join(" ").toLowerCase();
    const isBlocked = status === "bloqueada" || flagsText.includes("bloqueada");
    const isReview = category === "review" || reviewStatus === "en_revision" || reviewStatus === "pendiente_revision" || flagsText.includes("pendiente de revisión");
    const needsGeo = category === "geo" || flagsText.includes("geoevidencia");
    const needsEvidence = category === "evidence" || flagsText.includes("sin evidencia");
    const isOverdue = category === "overdue" || flagsText.includes("vencida");
    const isGovernance = category === "governance" || flagsText.includes("sin revisor");
    const isPlanning = category === "planning" || flagsText.includes("sin fecha");

    if (isBlocked) {
        return {
            focus: "intervention",
            mode: "progress",
            buttonLabel: "Intervenir ahora",
            buttonTitle: "Abrir Task Detail PRO en el panel de intervención PMO para documentar causa, solicitar evidencia, convertir en riesgo o liberar bloqueo.",
            decisionIntent: "intervenir_bloqueo",
        };
    }

    if (isReview) {
        return {
            focus: "review",
            mode: "review",
            buttonLabel: "Dictaminar avance",
            buttonTitle: "Abrir Task Detail PRO en modo revisor para aprobar, rechazar o solicitar cambios con trazabilidad.",
            decisionIntent: "dictaminar_revision",
        };
    }

    if (needsGeo) {
        return {
            focus: "geo",
            mode: "progress",
            buttonLabel: "Completar trazabilidad",
            buttonTitle: "Abrir Task Detail PRO en evidencia/georreferencia para completar soporte territorial o registrar excepción.",
            decisionIntent: "completar_geo",
        };
    }

    if (needsEvidence) {
        return {
            focus: "evidence",
            mode: "progress",
            buttonLabel: "Solicitar evidencia",
            buttonTitle: "Abrir Task Detail PRO en evidencia para solicitar o cargar soporte documental.",
            decisionIntent: "solicitar_evidencia",
        };
    }

    if (isOverdue || isPlanning) {
        return {
            focus: "reschedule",
            mode: "progress",
            buttonLabel: "Revisar fecha",
            buttonTitle: "Abrir Task Detail PRO para revisar vencimiento, reprogramar compromiso o justificar la desviación.",
            decisionIntent: "revisar_fecha",
        };
    }

    if (isGovernance) {
        return {
            focus: "governance",
            mode: "progress",
            buttonLabel: "Asignar gobierno",
            buttonTitle: "Abrir Task Detail PRO para revisar responsable, revisor, fecha y reglas de seguimiento PMO.",
            decisionIntent: "asignar_gobierno",
        };
    }

    return {
        focus: "summary",
        mode: "progress",
        buttonLabel: "Revisar tarea",
        buttonTitle: "Abrir Task Detail PRO para revisar el contexto operativo y decidir la siguiente acción.",
        decisionIntent: "revisar_tarea",
    };
}

function ActionCard({ action, onOpenTask, onOpenKanban }) {
    const sev = severityStyle(action.severity);
    const pr = priorityStyle(action.priority);
    const score = safeNumber(action.decision_score);
    const routeContext = getDecisionRouteContext(action);

    const flagTone = (flag) => {
        const f = String(flag || "").toLowerCase();
        if (f.includes("vencida") || f.includes("crítica") || f.includes("inconsistente")) return "danger";
        if (f.includes("evidencia") || f.includes("fecha") || f.includes("revisor")) return "warning";
        if (f.includes("revisión")) return "purple";
        return "neutral";
    };

    return (
        <div
            style={{
                borderRadius: 24,
                background: "#FFFFFF",
                border: `1px solid ${sev.border}`,
                boxShadow: score >= 90 ? "0 18px 45px rgba(220,38,38,.10)" : "0 14px 32px rgba(15,23,42,.05)",
                padding: 16,
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 9 }}>
                        <Pill
                            tone={action.severity === "critical" ? "danger" : action.severity === "high" ? "orange" : action.severity === "medium" ? "warning" : "neutral"}
                            title={severityTooltip(action.severity)}
                        >
                            {sev.icon} {sev.label}
                        </Pill>
                        <Pill tone="info" title={categoryTooltip(action.category)}>{categoryLabel(action.category)}</Pill>
                        <span
                            title={priorityTooltip(action.priority)}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "6px 9px",
                                borderRadius: 999,
                                background: pr.bg,
                                color: pr.color,
                                border: `1px solid ${pr.border}`,
                                fontSize: 10,
                                fontWeight: 950,
                                cursor: "help",
                            }}
                        >
                            {label(action.priority)}
                        </span>
                    </div>

                    <h3
                        style={{
                            margin: 0,
                            color: "#0F172A",
                            fontSize: 15,
                            fontWeight: 950,
                            lineHeight: 1.25,
                        }}
                    >
                        {action.wbs_code ? `${action.wbs_code} · ` : ""}
                        {cleanText(action.task_name || "Tarea sin nombre")}
                    </h3>

                    <div style={{ marginTop: 5, color: "#64748B", fontSize: 11, fontWeight: 800 }}>
                        {action.project_code || "PRJ"} · {action.project_name || "Sin proyecto"}
                    </div>
                </div>

                <div
                    title={scoreTooltip()}
                    style={{
                        minWidth: 70,
                        textAlign: "center",
                        borderRadius: 16,
                        background: sev.bg,
                        border: `1px solid ${sev.border}`,
                        color: sev.color,
                        padding: "8px 9px",
                        cursor: "help",
                    }}
                >
                    <div style={{ fontSize: 22, fontWeight: 950, letterSpacing: "-.04em", lineHeight: 1 }}>
                        {score.toFixed(0)}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 9, fontWeight: 950, textTransform: "uppercase" }}>
                        score
                    </div>
                </div>
            </div>

            <div style={{ marginTop: 13 }}>
                <ProgressBar value={action.progress_percent} color={sev.accent} />
                <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", color: "#64748B", fontSize: 11, fontWeight: 800 }}>
                    <span title={progressTooltip(action)} style={{ cursor: "help" }}>Avance {safeNumber(action.progress_percent).toFixed(0)}%</span>
                    <span title={dueDateTooltip(action)} style={{ cursor: "help" }}>{action.due_date ? `Fecha: ${formatDate(action.due_date)}` : "Sin fecha objetivo"}</span>
                </div>
            </div>

            <div
                style={{
                    marginTop: 13,
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 10,
                }}
            >
                <UserMini name={action.owner_name} />
                <UserMini name={action.reviewer_name || "Sin revisor"} />
            </div>

            <div style={{ marginTop: 13, display: "flex", gap: 7, flexWrap: "wrap" }}>
                {(action.flags || []).map((flag, index) => (
                    <Pill key={`${flag}-${index}`} tone={flagTone(flag)} title={flagTooltip(flag)}>
                        {flag}
                    </Pill>
                ))}
            </div>

            <div
                style={{
                    marginTop: 13,
                    borderRadius: 18,
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    padding: 12,
                    color: "#334155",
                    fontSize: 12,
                    fontWeight: 850,
                    lineHeight: 1.45,
                }}
            >
                <strong title={recommendedActionTooltip()} style={{ cursor: "help" }}>Acción sugerida:</strong> {cleanText(action.recommended_action)}
            </div>

            <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                    type="button"
                    title={routeContext.buttonTitle}
                    onClick={() => onOpenTask?.(action)}
                    style={primaryButton}
                >
                    {routeContext.buttonLabel}
                </button>

                <button
                    type="button"
                    title="Ver esta tarea dentro del tablero Kanban para revisar su estado operativo y contexto visual."
                    onClick={() => onOpenKanban?.(action)}
                    style={secondaryButton}
                >
                    Ver en Kanban
                </button>
            </div>
        </div>
    );
}

function SectionTitle({ title, subtitle }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <h2
                style={{
                    margin: 0,
                    color: "#0F172A",
                    fontSize: 18,
                    fontWeight: 950,
                    letterSpacing: "-.03em",
                }}
            >
                {title}
            </h2>
            <div style={{ marginTop: 5, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                {subtitle}
            </div>
        </div>
    );
}

function EmptyBox({ text }) {
    return (
        <div
            style={{
                borderRadius: 18,
                border: "1px dashed #CBD5E1",
                background: "#F8FAFC",
                padding: 16,
                color: "#64748B",
                fontSize: 12,
                fontWeight: 800,
                textAlign: "center",
            }}
        >
            {text}
        </div>
    );
}

const primaryButton = {
    height: 38,
    padding: "0 15px",
    borderRadius: 999,
    border: "1px solid #0F172A",
    background: "#0F172A",
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 12px 26px rgba(15,23,42,.16)",
};

const secondaryButton = {
    height: 38,
    padding: "0 15px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#0F172A",
    fontSize: 11,
    fontWeight: 950,
    cursor: "pointer",
};


function ScopeNotice({ scope, fallbackLabel, fallbackDescription }) {
    const labelText = scope?.label || fallbackLabel;
    const descriptionText = scope?.description || fallbackDescription;

    return (
        <div
            style={{
                margin: "0 0 18px",
                borderRadius: 20,
                background: "#FFFFFF",
                border: "1px solid #DBEAFE",
                padding: "14px 16px",
                boxShadow: "0 12px 28px rgba(37,99,235,.06)",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
            }}
        >
            <div
                style={{
                    width: 34,
                    height: 34,
                    borderRadius: 14,
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 950,
                    flex: "0 0 auto",
                }}
            >
                ⌖
            </div>
            <div>
                <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950 }}>
                    Alcance de la vista: {labelText}
                </div>
                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                    {descriptionText}
                </div>
            </div>
        </div>
    );
}

export default function DecisionCenterPage() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState("all");
    const [severityFilter, setSeverityFilter] = useState("all");
    const [decisionTypeFilter, setDecisionTypeFilter] = useState("all");

    const loadDecisionCenter = async () => {
        try {
            setLoading(true);
            setError("");

            const token = localStorage.getItem("cani_auth") || "";
            const qp = new URLSearchParams();

            if (selectedProjectId && selectedProjectId !== "all") {
                qp.set("project_id", selectedProjectId);
            }

            const response = await fetch(`${API_BASE}/pmo_decision_center.php${qp.toString() ? `?${qp.toString()}` : ""}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : "",
                    "X-CANI-AUTH": token || "",
                },
            });

            const text = await response.text();

            if (!text || !text.trim()) {
                throw new Error("Respuesta vacía del Centro de Decisiones PMO");
            }

            const json = JSON.parse(text);

            if (!response.ok || !json.success) {
                throw new Error(json.message || json.detail || "No se pudo cargar Centro de Decisiones PMO");
            }

            setData(json.data || null);
        } catch (e) {
            setError(e.message || "Error al cargar Centro de Decisiones PMO");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDecisionCenter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectId]);

    const summary = data?.summary || {};
    const scope = data?.scope || {};
    const projects = data?.project_options || data?.projects || [];
    const actions = data?.actions || [];

    const selectedProjectName = useMemo(() => {
        if (selectedProjectId === "all") return getScopeLabel(selectedProjectId);
        const found = projects.find((p) => String(p.id || p.project_id) === String(selectedProjectId));
        return found ? `${found.code || "PRJ"} · ${found.name}` : getScopeLabel(selectedProjectId);
    }, [projects, selectedProjectId]);

    const insight = useMemo(() => {
        return buildDecisionInsight({ summary, actions, selectedProjectName });
    }, [summary, actions, selectedProjectName]);

    const filteredActions = useMemo(() => {
        return actions.filter((a) => {
            const severityOk = severityFilter === "all" || String(a.severity) === String(severityFilter);
            const typeOk = actionMatchesDecisionFilter(a, decisionTypeFilter);
            return severityOk && typeOk;
        });
    }, [actions, severityFilter, decisionTypeFilter]);

    const openTask = (action) => {
        if (!action?.task_id) return;

        const routeContext = getDecisionRouteContext(action);
        const qp = new URLSearchParams();

        qp.set("mode", routeContext.mode || "progress");
        qp.set("focus", routeContext.focus || "summary");
        qp.set("decision_intent", routeContext.decisionIntent || "revisar_tarea");
        qp.set("source", "decision_center");
        qp.set("return_to", selectedProjectId !== "all" ? `/decision-center?project_id=${selectedProjectId}` : "/decision-center");
        qp.set("return_label", "Centro de Decisiones PMO");

        navigate(`/my-work/task/${action.task_id}?${qp.toString()}`);
    };

    const openKanban = (action) => {
        const qp = new URLSearchParams();

        if (selectedProjectId && selectedProjectId !== "all") {
            qp.set("project_id", selectedProjectId);
        } else if (action?.project_id) {
            qp.set("project_id", action.project_id);
        }

        const routeContext = getDecisionRouteContext(action);

        if (routeContext.kanbanGovernance) {
            qp.set("governance", routeContext.kanbanGovernance);
        } else if (action?.category === "governance") {
            qp.set("governance", "without_reviewer");
        } else if (action?.category === "planning") {
            qp.set("governance", "without_due_date");
        } else if (action?.category === "evidence") {
            qp.set("governance", "without_evidence");
        } else if (action?.category === "geo") {
            qp.set("governance", "without_geo");
        } else if (action?.category === "inconsistent") {
            qp.set("governance", "inconsistent");
        } else {
            qp.set("governance", "overdue");
        }

        if (action?.task_id) {
            qp.set("task_id", action.task_id);
        }

        qp.set("source", "decision_center");

        qp.set("return_to", selectedProjectId !== "all" ? `/decision-center?project_id=${selectedProjectId}` : "/decision-center");
        qp.set("return_label", "Centro de Decisiones PMO");

        navigate(`/kanban?${qp.toString()}`);
    };

    if (loading) {
        return (
            <div
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                Cargando Centro de Decisiones PMO...
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    borderRadius: 20,
                    background: "#FFF1F2",
                    border: "1px solid #FECDD3",
                    color: "#BE123C",
                    padding: 20,
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                {error}
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "radial-gradient(circle at top left, rgba(37,99,235,.10), transparent 28%), #F8FAFC",
            }}
        >
            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 28,
                    padding: 26,
                    background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 58%, #EEF2FF 100%)",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 20px 50px rgba(15,23,42,.07)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 20,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: "#FFF1F2",
                            border: "1px solid #FECDD3",
                            color: "#BE123C",
                            fontSize: 11,
                            fontWeight: 950,
                            letterSpacing: ".06em",
                            textTransform: "uppercase",
                            marginBottom: 14,
                        }}
                    >
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#DC2626" }} />
                        Decisiones / Intervención / PMO
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            fontSize: 34,
                            lineHeight: 1.05,
                            color: "#0F172A",
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                        }}
                    >
                        Centro de Decisiones PMO
                    </h1>

                    <p
                        style={{
                            margin: "10px 0 0",
                            color: "#64748B",
                            fontSize: 14,
                            lineHeight: 1.5,
                            maxWidth: 840,
                            fontWeight: 700,
                        }}
                    >
                        Bandeja ejecutiva de acciones urgentes del proyecto o Portafolio PMO: prioriza vencimientos, tareas críticas, falta de revisor, evidencia pendiente, falta de geoevidencia o avance inconsistente.
                    </p>
                </div>

                <div
                    style={{
                        minWidth: 260,
                        borderRadius: 24,
                        background: "#0F172A",
                        color: "#FFFFFF",
                        padding: 18,
                        boxShadow: "0 20px 45px rgba(15,23,42,.22)",
                    }}
                >
                    <div style={{ color: "#94A3B8", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>
                        Acciones para decidir
                    </div>
                    <div style={{ marginTop: 8, fontSize: 42, fontWeight: 950, letterSpacing: "-.06em" }}>
                        {summary.total_actions || 0}
                    </div>
                    <div style={{ marginTop: 6, color: "#CBD5E1", fontSize: 12, fontWeight: 800 }}>
                        {summary.critical_actions || 0} críticas · {summary.high_actions || 0} altas · {summary.overdue_actions || 0} vencidas
                    </div>
                </div>
            </section>

            <ScopeNotice
                scope={scope}
                fallbackLabel="Proyecto completo / Portafolio PMO"
                fallbackDescription="Muestra acciones pendientes de todo el proyecto o portafolio, aunque no esten asignadas directamente al usuario actual."
            />

            <section
                style={{
                    marginBottom: 18,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <select
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    style={{
                        height: 42,
                        minWidth: 320,
                        borderRadius: 999,
                        border: "1px solid #E2E8F0",
                        background: "#FFFFFF",
                        padding: "0 16px",
                        color: "#0F172A",
                        fontSize: 13,
                        fontWeight: 800,
                        outline: "none",
                        boxShadow: "0 12px 25px rgba(15,23,42,.04)",
                    }}
                >
                    <option value="all">Portafolio PMO</option>
                    {projects.map((p) => (
                        <option key={p.id || p.project_id} value={p.id || p.project_id}>
                            {p.code || "PRJ"} · {p.name || p.project_name}
                        </option>
                    ))}
                </select>

                <button type="button" onClick={loadDecisionCenter} style={primaryButton}>
                    Actualizar
                </button>
            </section>

            <section
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 16,
                    marginBottom: 18,
                }}
            >
                <KpiCard title="Acciones" value={summary.total_actions || 0} subtitle="Total a decidir" color="#0F172A" icon="▦" />
                <KpiCard title="Críticas" value={summary.critical_actions || 0} subtitle="Atender primero" color="#DC2626" icon="!" />
                <KpiCard title="Altas" value={summary.high_actions || 0} subtitle="Intervención próxima" color="#EA580C" icon="▲" />
                <KpiCard title="Vencidas" value={summary.overdue_actions || 0} subtitle="Deuda operativa" color="#BE123C" icon="⏱" />
                <KpiCard title="Gobierno" value={summary.governance_actions || 0} subtitle="Revisor / fecha" color="#7C3AED" icon="◌" />
                <KpiCard title="Evidencia" value={summary.evidence_actions || 0} subtitle="Soporte pendiente" color="#2563EB" icon="⌖" />
            </section>

            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 28,
                    background: insight.tone.bg,
                    border: `1px solid ${insight.tone.border}`,
                    padding: 22,
                    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 18,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                    }}
                >
                    <div style={{ maxWidth: 880 }}>
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "6px 10px",
                                borderRadius: 999,
                                background: "#FFFFFF",
                                border: `1px solid ${insight.tone.border}`,
                                color: insight.tone.color,
                                fontSize: 11,
                                fontWeight: 950,
                                textTransform: "uppercase",
                                letterSpacing: ".04em",
                                marginBottom: 12,
                            }}
                        >
                            <span>{insight.tone.icon}</span>
                            Lectura PMO automática
                        </div>

                        <h2
                            style={{
                                margin: 0,
                                color: insight.tone.color,
                                fontSize: 22,
                                fontWeight: 950,
                                letterSpacing: "-.03em",
                            }}
                        >
                            {insight.title}
                        </h2>

                        <div style={{ marginTop: 6, color: "#334155", fontSize: 12, fontWeight: 850 }}>
                            Consulta actual: {insight.selectedProjectName}
                        </div>

                        <p
                            style={{
                                margin: "12px 0 0",
                                color: "#334155",
                                fontSize: 14,
                                lineHeight: 1.55,
                                fontWeight: 750,
                            }}
                        >
                            {insight.message}
                        </p>

                        <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
                            {insight.bullets.map((item, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: "flex-start",
                                        color: "#334155",
                                        fontSize: 13,
                                        fontWeight: 800,
                                        lineHeight: 1.35,
                                    }}
                                >
                                    <span style={{ color: insight.tone.color, fontWeight: 950 }}>•</span>
                                    <span>{cleanText(item)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div
                        style={{
                            minWidth: 250,
                            borderRadius: 22,
                            background: "#FFFFFF",
                            border: `1px solid ${insight.tone.border}`,
                            padding: 16,
                            boxShadow: "0 12px 28px rgba(15,23,42,.05)",
                        }}
                    >
                        <div style={{ color: "#64748B", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".04em" }}>
                            Acción PMO sugerida
                        </div>
                        <div style={{ marginTop: 8, color: "#0F172A", fontSize: 14, fontWeight: 900, lineHeight: 1.45 }}>
                            {insight.action}
                        </div>
                    </div>
                </div>
            </section>

            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 22,
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    padding: 16,
                    boxShadow: "0 12px 28px rgba(15,23,42,.04)",
                }}
            >
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: 14,
                            background: "#EFF6FF",
                            color: "#1D4ED8",
                            border: "1px solid #BFDBFE",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 16,
                            fontWeight: 950,
                            flex: "0 0 auto",
                        }}
                    >
                        i
                    </div>
                    <div>
                        <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950, marginBottom: 5 }}>
                            Criterio PMO de decisión
                        </div>
                        <div style={{ color: "#475569", fontSize: 12, fontWeight: 750, lineHeight: 1.55 }}>
                            El Centro de Decisiones no sustituye la gestión del PM: prioriza lo que requiere intervención. Una tarea puede aparecer por vencimiento, falta de revisor, falta de evidencia, falta de geoevidencia o avance inconsistente. La lectura correcta es: <strong>qué decisión debe tomarse ahora para desbloquear, validar o cerrar formalmente el trabajo.</strong>
                        </div>
                    </div>
                </div>
            </section>

            <section
                style={{
                    borderRadius: 28,
                    background: "rgba(255,255,255,.92)",
                    border: "1px solid #E2E8F0",
                    padding: 18,
                    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 14 }}>
                    <div>
                        <SectionTitle
                            title="Lista priorizada de decisiones"
                            subtitle="Acciones ordenadas por severidad y score de decisión. Atiende primero las críticas y altas."
                        />

                        <div style={{ marginTop: -6, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ fontSize: 10, fontWeight: 950, color: "#64748B", textTransform: "uppercase", letterSpacing: ".04em" }}>
                                Guía:
                            </span>
                            <Pill tone="danger" title="Score 90 o más: acción crítica que debe atenderse primero.">90+ crítica</Pill>
                            <Pill tone="orange" title="Score de 65 a 89: acción alta que requiere intervención próxima.">65–89 alta</Pill>
                            <Pill tone="warning" title="Score de 35 a 64: acción media que requiere seguimiento PMO.">35–64 media</Pill>
                            <Pill tone="neutral" title="Score de 0 a 34: observación baja de seguimiento o trazabilidad.">0–34 baja</Pill>
                        </div>
                    </div>

                    <div style={{ display: "grid", gap: 8, justifyItems: "end" }}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            {[
                                { key: "all", label: "Todas" },
                                { key: "critical", label: "Críticas" },
                                { key: "high", label: "Altas" },
                                { key: "medium", label: "Medias" },
                                { key: "low", label: "Bajas" },
                            ].map((item) => {
                                const active = severityFilter === item.key;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        onClick={() => setSeverityFilter(item.key)}
                                        title={filterTooltip(item.key)}
                                        style={{
                                            height: 34,
                                            padding: "0 12px",
                                            borderRadius: 999,
                                            border: active ? "1px solid #0F172A" : "1px solid #E2E8F0",
                                            background: active ? "#0F172A" : "#FFFFFF",
                                            color: active ? "#FFFFFF" : "#475569",
                                            fontSize: 11,
                                            fontWeight: 950,
                                            cursor: "pointer",
                                        }}
                                    >
                                        {item.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            {[
                                { key: "all", label: "Todas las causas" },
                                { key: "blocked", label: "Bloqueadas" },
                                { key: "overdue", label: "Vencidas" },
                                { key: "evidence", label: "Sin evidencia" },
                                { key: "geo", label: "Sin geo" },
                                { key: "review", label: "En revisión" },
                            ].map((item) => {
                                const active = decisionTypeFilter === item.key;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        onClick={() => setDecisionTypeFilter(item.key)}
                                        title={decisionFilterTooltip(item.key)}
                                        style={{
                                            height: 32,
                                            padding: "0 11px",
                                            borderRadius: 999,
                                            border: active ? "1px solid #2563EB" : "1px solid #E2E8F0",
                                            background: active ? "#EFF6FF" : "#FFFFFF",
                                            color: active ? "#1D4ED8" : "#475569",
                                            fontSize: 10,
                                            fontWeight: 950,
                                            cursor: "pointer",
                                        }}
                                    >
                                        {item.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 14 }}>
                    {filteredActions.length ? (
                        filteredActions.map((action) => (
                            <ActionCard
                                key={action.task_id}
                                action={action}
                                onOpenTask={openTask}
                                onOpenKanban={openKanban}
                            />
                        ))
                    ) : (
                        <EmptyBox text="No hay acciones para esta combinación de filtros." />
                    )}
                </div>
            </section>
        </div>
    );
}
